import { useEffect } from 'react'
import { useOrganization, useOrganizationList } from '@clerk/clerk-react'
import { Building2, RotateCw } from 'lucide-react'
import { toast } from 'sonner'

import { CommandGroup } from '@/components/ui/command'
import { ORG_SWITCHER_MEMBERSHIPS_PAGE_SIZE } from '@/config/constants'

import { PaletteRow } from './PaletteRow'
import { ORG_VALUE_PREFIX } from './paletteFilter'

const SWITCH_TOAST_ID = 'command-palette-org-switch'

/**
 * "Switch organization" rows for the command palette — the same memberships
 * the sidebar's Clerk `<OrganizationSwitcher>` offers, minus the active org.
 *
 * Render only for users who hold `orgSwitcher.switch`, and only once they have
 * typed something: mounting is what kicks off the membership fetch. Clerk
 * dedupes that request for 60s, so quick reopens don't refetch; a mount after
 * that revalidates.
 *
 * Switching does no navigation of its own. `OrgProvider` in `App.tsx` sees the
 * active org change and reloads the app from `/`, exactly as it does for the
 * sidebar switcher.
 */
export function OrgSwitchGroup({ onSwitch }: { onSwitch: () => void }) {
  const { organization: activeOrg } = useOrganization()
  const { isLoaded, userMemberships, setActive } = useOrganizationList({
    userMemberships: {
      infinite: true,
      pageSize: ORG_SWITCHER_MEMBERSHIPS_PAGE_SIZE,
    },
  })

  // Clerk pages memberships; a partial list would silently hide orgs, so keep
  // pulling until there is nothing left. A failed page leaves `hasNextPage`
  // true, so stop on error rather than retrying in a tight loop against a
  // rate-limited Clerk — the retry row below hands that choice to the user.
  const { hasNextPage, isFetching, isError, fetchNext, revalidate } =
    userMemberships
  useEffect(() => {
    if (hasNextPage && !isFetching && !isError) fetchNext()
  }, [hasNextPage, isFetching, isError, fetchNext])

  if (!isLoaded) return null

  // Every org in Hoku has a slug — API calls are keyed on it — so an org
  // without one could not be used anyway. Requiring it keeps `value` free of
  // the random Clerk id, whose letters would fuzzy-match unrelated queries.
  const orgs = userMemberships.data.flatMap(({ organization: org }) =>
    org.id !== activeOrg?.id && org.slug
      ? [{ id: org.id, name: org.name, slug: org.slug, keywords: [org.name] }]
      : []
  )

  if (orgs.length === 0 && !isError) return null

  const switchTo = async (org: { id: string; name: string }) => {
    onSwitch()
    // The reload only starts once Clerk answers; without this a slow round
    // trip looks like nothing happened and invites a second switch.
    toast.loading(`Switching to ${org.name}…`, { id: SWITCH_TOAST_ID })
    try {
      await setActive({ organization: org.id })
    } catch {
      toast.error("Couldn't switch organization. Try again.", {
        id: SWITCH_TOAST_ID,
      })
    }
  }

  return (
    // Force-mounted on error so the retry row survives whatever was typed.
    <CommandGroup forceMount={isError} heading="Switch organization">
      {orgs.map((org) => (
        <PaletteRow
          key={org.id}
          icon={Building2}
          label={org.name}
          // Slugs are unique in Clerk, so this stays a unique cmdk value. The
          // name is scored separately from it — see `paletteFilter`.
          value={`${ORG_VALUE_PREFIX}${org.slug}`}
          keywords={org.keywords}
          // The group is force-mounted while the retry row shows; without an
          // explicit false every org row would inherit that and skip the
          // filter, listing all of them for any query.
          forceMount={false}
          hint={org.slug}
          onSelect={() => {
            void switchTo(org)
          }}
        />
      ))}
      {/* The palette's "No matching view." can show above this row: it is
          about views, and this is about orgs, so both are true at once. */}
      {isError && (
        <PaletteRow
          icon={RotateCw}
          label={
            orgs.length > 0
              ? "Couldn't load every organization. Retry"
              : "Couldn't load organizations. Retry"
          }
          value={`${ORG_VALUE_PREFIX}retry`}
          forceMount
          onSelect={() => {
            void revalidate()
          }}
        />
      )}
    </CommandGroup>
  )
}
